// Central API configuration and base service for CareerCatalyst
// This file provides the foundation for all API communications

export const API_CONFIG = {
  BASE_URL: import.meta.env.VITE_API_URL || 'http://localhost:8001/api/v1',
  TIMEOUT: 360000, // 6 minutes - allows job analysis to complete (takes 2-3 min)
} as const;

// Token management utilities
export const tokenManager = {
  getToken: (): string | null => {
    return localStorage.getItem('access_token');
  },

  setToken: (token: string): void => {
    localStorage.setItem('access_token', token);
    // Store token timestamp for proactive refresh
    localStorage.setItem('token_timestamp', Date.now().toString());
  },

  removeToken: (): void => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('token_timestamp');
  },

  getRefreshToken: (): string | null => {
    return localStorage.getItem('refresh_token');
  },

  setRefreshToken: (token: string): void => {
    localStorage.setItem('refresh_token', token);
  },

  removeRefreshToken: (): void => {
    localStorage.removeItem('refresh_token');
  },

  getTokenTimestamp: (): number | null => {
    const timestamp = localStorage.getItem('token_timestamp');
    return timestamp ? parseInt(timestamp, 10) : null;
  },

  isTokenExpiringSoon: (bufferMinutes: number = 1): boolean => {
    const timestamp = tokenManager.getTokenTimestamp();
    if (!timestamp) return true;

    const tokenAgeMs = Date.now() - timestamp;
    const tokenAgeMinutes = tokenAgeMs / 1000 / 60;

    // Token expires at 15 minutes, refresh if within buffer
    return tokenAgeMinutes >= (15 - bufferMinutes);
  }
};

// API Error types
export class APIError extends Error {
  constructor(
    message: string,
    public status: number,
    public response?: any
  ) {
    super(message);
    this.name = 'APIError';
  }
}

// Token refresh lock to prevent simultaneous refresh attempts
let isRefreshing = false;
let refreshPromise: Promise<string> | null = null;

// Request headers builder
export const getAuthHeaders = (): Record<string, string> => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  const token = tokenManager.getToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return headers;
};

// Centralized token refresh with lock mechanism
const refreshAccessToken = async (): Promise<string> => {
  // If already refreshing, wait for that promise to complete
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  // Set lock and create refresh promise
  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      const refreshToken = tokenManager.getRefreshToken();
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const refreshResponse = await fetch(`${API_CONFIG.BASE_URL}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (!refreshResponse.ok) {
        throw new Error('Refresh token expired or invalid');
      }

      const tokenData = await refreshResponse.json();
      tokenManager.setToken(tokenData.access_token);

      if (tokenData.refresh_token) {
        tokenManager.setRefreshToken(tokenData.refresh_token);
      }

      return tokenData.access_token;
    } catch (error) {
      // Refresh failed - remove tokens and redirect to login
      tokenManager.removeToken();
      tokenManager.removeRefreshToken();

      // Redirect to the landing page, which opens the auth dialog on this flag
      if (window.location.pathname !== '/') {
        window.location.href = '/?session_expired=true';
      }

      throw error;
    } finally {
      // Release lock
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

// Proactive token refresh - call this before making important requests
export const ensureValidToken = async (): Promise<void> => {
  if (!tokenManager.getToken()) {
    return; // No token, user not authenticated
  }

  // Check if token is expiring soon (within 1 minute)
  if (tokenManager.isTokenExpiringSoon(1)) {
    try {
      await refreshAccessToken();
    } catch (error) {
      // Refresh failed, will redirect to login
      throw error;
    }
  }
};

// Response handler with automatic error handling
export const handleResponse = async <T>(response: Response): Promise<T> => {
  if (!response.ok) {
    // Default user-friendly messages for common HTTP errors
    const defaultMessages: Record<number, string> = {
      401: 'Invalid email or password',
      403: 'Access denied',
      404: 'Resource not found',
      422: 'Please check your input and try again',
      500: 'Server error. Please try again later.',
    };

    let errorMessage = defaultMessages[response.status] || `Error: ${response.statusText}`;
    let errorResponse: any = null;

    try {
      // Use text() first then parse - more reliable than direct json()
      const text = await response.text();
      if (text) {
        errorResponse = JSON.parse(text);

        // Handle our custom error format: {success, error, details}
        if (errorResponse.error) {
          errorMessage = errorResponse.error;

          // If validation error with details, extract first detail message
          if (errorResponse.details && Array.isArray(errorResponse.details) && errorResponse.details.length > 0) {
            const detail = errorResponse.details[0];
            if (detail.message) {
              // Clean up "Value error, " prefix from Pydantic validators
              errorMessage = detail.message.replace(/^Value error,\s*/i, '');
            }
          }
        }
        // Fallback to detail field (standard FastAPI format)
        else if (errorResponse.detail) {
          errorMessage = typeof errorResponse.detail === 'string'
            ? errorResponse.detail
            : 'Validation failed';
        } else if (errorResponse.message) {
          errorMessage = errorResponse.message;
        }
      }
    } catch {
      // JSON parsing failed, use default message
    }

    throw new APIError(errorMessage, response.status, errorResponse);
  }

  // 204 No Content has no body to parse
  if (response.status === 204) {
    return undefined as T;
  }
  return response.json();
};

// Base API request function with automatic token handling
export const apiRequest = async <T>(
  endpoint: string,
  options: RequestInit & { skipDefaultHeaders?: boolean; skipProactiveRefresh?: boolean; skipAuthRetry?: boolean } = {}
): Promise<T> => {
  const url = `${API_CONFIG.BASE_URL}${endpoint}`;

  // Proactive token refresh BEFORE making the request (unless explicitly skipped)
  if (!options.skipProactiveRefresh && tokenManager.getToken()) {
    try {
      await ensureValidToken();
    } catch (error) {
      // ensureValidToken already redirects to login on failure
      throw new APIError('Session expired. Redirecting to login...', 401);
    }
  }

  const config: RequestInit = {
    ...options,
    headers: options.skipDefaultHeaders
      ? options.headers  // For uploads: use provided headers only (no Content-Type)
      : {
          ...getAuthHeaders(),  // For normal requests: merge with default headers
          ...(options.headers || {}),
        },
  };

  try {
    const response = await fetch(url, config);

    // Handle 401 errors by attempting token refresh (fallback if proactive refresh didn't work)
    // Skip this for auth endpoints (login, register) - they should return the actual error
    const isAuthEndpoint = endpoint.startsWith('/auth/login') || endpoint.startsWith('/auth/register');

    if (response.status === 401 && !isAuthEndpoint && !options.skipAuthRetry && tokenManager.getToken()) {
      try {
        // Use centralized refresh with lock
        await refreshAccessToken();

        // Retry the original request with the new token
        const retryConfig: RequestInit = {
          ...config,
          headers: options.skipDefaultHeaders
            ? {
                Authorization: `Bearer ${tokenManager.getToken()}`,
                ...(options.headers || {}),
              }
            : {
                ...getAuthHeaders(),
                ...(options.headers || {}),
              },
        };

        const retryResponse = await fetch(url, retryConfig);
        return handleResponse<T>(retryResponse);
      } catch (refreshError) {
        // refreshAccessToken already handles token removal and redirect
        throw new APIError('Session expired. Please log in again.', 401);
      }
    }

    return handleResponse<T>(response);
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`, 0);
  }
};

// HTTP method convenience functions
export const api = {
  get: <T>(endpoint: string, options?: RequestInit): Promise<T> =>
    apiRequest<T>(endpoint, { ...options, method: 'GET' }),

  post: <T>(endpoint: string, data?: any, options?: RequestInit): Promise<T> =>
    apiRequest<T>(endpoint, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    }),

  put: <T>(endpoint: string, data?: any, options?: RequestInit): Promise<T> =>
    apiRequest<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    }),

  patch: <T>(endpoint: string, data?: any, options?: RequestInit): Promise<T> =>
    apiRequest<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    }),

  delete: <T>(endpoint: string, options?: RequestInit): Promise<T> =>
    apiRequest<T>(endpoint, { ...options, method: 'DELETE' }),

  // Special method for file uploads
  upload: <T>(endpoint: string, formData: FormData, options?: RequestInit): Promise<T> => {
    // For file uploads, only add Authorization header (no Content-Type)
    // Browser will automatically set Content-Type with correct multipart boundary
    const token = tokenManager.getToken();
    const uploadHeaders: Record<string, string> = {};
    if (token) {
      uploadHeaders['Authorization'] = `Bearer ${token}`;
    }

    return apiRequest<T>(endpoint, {
      ...options,
      method: 'POST',
      headers: uploadHeaders,
      body: formData,
      // Add flag to prevent apiRequest from adding Content-Type
      skipDefaultHeaders: true,
    } as any);
  },
};

// Background token refresh manager
let refreshTimerId: NodeJS.Timeout | null = null;

export const startTokenRefreshTimer = (): void => {
  // Clear any existing timer
  if (refreshTimerId) {
    clearInterval(refreshTimerId);
  }

  // Check token every 60 seconds
  refreshTimerId = setInterval(async () => {
    if (!tokenManager.getToken()) {
      // No token, stop checking
      stopTokenRefreshTimer();
      return;
    }

    // Refresh if token expires in 2 minutes or less (at 13+ minutes)
    if (tokenManager.isTokenExpiringSoon(2)) {
      try {
        await refreshAccessToken();
      } catch (error) {
        console.error('Background token refresh failed:', error);
        stopTokenRefreshTimer();
      }
    }
  }, 60000); // Check every 60 seconds
};

export const stopTokenRefreshTimer = (): void => {
  if (refreshTimerId) {
    clearInterval(refreshTimerId);
    refreshTimerId = null;
  }
};

// Auto-start timer if token exists on module load
if (tokenManager.getToken()) {
  startTokenRefreshTimer();
}
